import { type Is, isArrayNotEmpty, type ReadonlyDate, type ReadonlyNonEmptyArray, type ReadonlyRecord } from '../utils'

import type { BufferType } from '.'

/**
 * Type that represents a topic, with its technology specific code kept with-in
 */
export interface Topic<Rootable extends boolean = true> {
    /**
     * The full path of the topic
     *
     * Null if topic is root
     */
    readonly fullPath: string | Is<Rootable, true, null, never>

    /**
     * The levels of the topic, already split for convenience
     *
     * Null if topic is root
     */
    readonly levels: readonly string[] | Is<Rootable, true, null, never>

    /**
     * The last level of the topic
     * If the topic is empty, it will yield an empty string
     *
     * Null if topic is root
     */
    readonly lastLevel: string | Is<Rootable, true, null, never>

    /**
     * If the current topic is valid
     */
    readonly valid: boolean

    /**
     * If this topic is exact or has a pattern with-in it
     *
     * Null if topic is root
     */
    readonly exact: boolean | Is<Rootable, true, null, never>

    /**
     * Create a new topic while appending a single level of the given path
     * @returns a tupple with the child and the rest of the topic
     * - if there was no topic left after the depth, then a null depth will be yielded
     */
    createChild(path: string): readonly [child: Topic | this, rest: string | null]

    /**
     * If any the given topics prefix this topic
     *
     * @example 'topic/' is prefixed by 'topic/endpoint/temperature'
     * @example 'topic/endpoint/temperature/' is not prefixed by 'topic/endpoint/temperature'
     */
    isPrefixedBy(topics: readonly string[]): boolean

    /**
     * @returns true if this topic can be sourced from any of the given topics
     * @example 'topic' is sourced from '#'
     * @example 'topic' is sourced from '+'
     * @example 'topic/foo' is sourced from 'topic/+'
     * @example 'topic/foo' is not sourced from 'topic/bar'
     */
    isSourcedFrom(topics: readonly string[]): boolean
}

type TopicMessage = Readonly<{
    topic: string
    payload: Buffer
    selectedType: BufferType
    possibleTypes: ReadonlyNonEmptyArray<BufferType>
    timestamp: ReadonlyDate
    /**
     * The subscribed topics that were listened to that yielded this message
     */
    sources: ReadonlyNonEmptyArray<string>
}>

export type TopicMessageEvent = Pick<TopicMessage, 'topic' | 'payload' | 'possibleTypes' | 'timestamp' | 'sources'>
export type LastTopicMessage = Pick<TopicMessage, 'payload' | 'selectedType' | 'possibleTypes' | 'sources'>
export type TailedTopicMessage = Pick<TopicMessage, 'topic' | 'payload' | 'selectedType' | 'timestamp'>

export type TopicSubscriptionError = 'invalid' | 'noPermissions' | 'unknown' | 'shared'

/**
 * Whenever a subscription to topics is made this object is returned
 *
 *  - subscribed: the topics that were actually subscribed
 *  - invalid: the topics that were not subscribed to and can never be subscribed to because they are invalid
 *  - noPermissions: the topics that were not subscribed to because the user lacks permissions
 *  - unknown: the topics that were not subscribed to due to unknown reasons and may be subscribed to if re-tried
 */
export type TopicSubscriptionResponse = ReadonlyRecord<'subscribed' | TopicSubscriptionError, readonly string[]>

export const isTopicSubscriptionResponseSuccessful = ({
    invalid,
    noPermissions,
    unknown,
    shared,
}: Pick<TopicSubscriptionResponse, TopicSubscriptionError>): boolean =>
    !isArrayNotEmpty(invalid) && !isArrayNotEmpty(noPermissions) && !isArrayNotEmpty(unknown) && !isArrayNotEmpty(shared)
